import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import InputBase from '@material-ui/core/InputBase';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@material-ui/core/styles';
import Container from 'common/Container';
import landingApiService from '../../../services/landingApi';
import { getTnoHeroGradient } from '../../../theme/tnoBrandColors';
import { sortMediaKits } from '../../../utils/mediaKitDisplay';
import MediaKitCard from '../TNOMediaKit/components/MediaKitCard';

const LandingMediaKitsIndex = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary?.main || '#00838f';
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await landingApiService.getProgramMediaKits();
        if (!cancelled) {
          setKits(Array.isArray(data) ? data : []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesQuery = (kit) => {
      const haystack = `${kit.name || ''} ${kit.slug || ''} ${kit.metaDescription || ''}`.toLowerCase();
      return haystack.includes(q);
    };
    const base = !q ? kits : kits.filter(matchesQuery);
    return sortMediaKits(base);
  }, [kits, query]);

  const heroBackground = getTnoHeroGradient(primary, secondary);

  return (
    <Box
      sx={{
        pb: 7,
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        background: `linear-gradient(180deg, ${alpha(primary, 0.04)} 0%, ${alpha(secondary, 0.06)} 16%, transparent 35%)`,
      }}
    >
      <Container>
        <Box pt={{ xs: 3, md: 4 }} pb={3}>
          <Button component={RouterLink} to="/" startIcon={<ArrowBackIcon />} color="primary" sx={{ mb: 3, borderRadius: 999 }}>
            Volver al media kit
          </Button>

          <Box
            sx={{
              borderRadius: { xs: 3, md: 4 },
              overflow: 'hidden',
              mb: 4,
              background: heroBackground,
              color: '#fff',
              px: { xs: 2.5, md: 4 },
              py: { xs: 4, md: 5 },
            }}
          >
            <Box component="img" src="/images/logos/logo_white.PNG" alt="TNO Network" sx={{ height: { xs: 42, md: 52 }, mb: 2 }} />
            <Typography sx={{ textTransform: 'uppercase', fontWeight: 700, letterSpacing: 2.5, fontSize: '0.8rem', opacity: 0.9 }}>
              TNO Network · Biblioteca comercial
            </Typography>
            <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '2.1rem', md: '2.9rem' }, lineHeight: 1.08, mt: 1, mb: 2 }}>
              Media Kits
            </Typography>
            <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, lineHeight: 1.65, maxWidth: 700, opacity: 0.92, mb: 3 }}>
              Todos los media kits de programas en un solo lugar. Busca por nombre o explora la biblioteca completa.
            </Typography>
            <Chip
              icon={<FolderOpenIcon />}
              label={`${kits.length} programas`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
            />
          </Box>

          <Box
            sx={{
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: 999,
              border: `1px solid ${alpha(primary, 0.2)}`,
              px: 2,
              py: 0.8,
              maxWidth: '100%',
              width: { xs: '100%', sm: 460 },
              boxSizing: 'border-box',
            }}
          >
            <SearchIcon color="action" fontSize="small" />
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, slug o descripción..."
              fullWidth
              inputProps={{ 'aria-label': 'Buscar media kits' }}
            />
          </Box>

          {loading ? (
            <Typography color="textSecondary">Cargando media kits...</Typography>
          ) : (
            <>
              {query.trim() ? (
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Resultados ({filtered.length})
                </Typography>
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                  Todos los media kits
                </Typography>
              )}
              <Grid container spacing={2.5}>
                {filtered.map((kit) => (
                  <Grid item xs={12} sm={6} md={4} key={kit._id || kit.slug}>
                    <MediaKitCard kit={kit} />
                  </Grid>
                ))}
              </Grid>
              {!filtered.length && (
                <Typography color="textSecondary" sx={{ mt: 2 }}>
                  {query.trim() ? 'No hay resultados para tu búsqueda.' : 'No hay media kits de programas disponibles.'}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
};

LandingMediaKitsIndex.propTypes = {
  themeMode: PropTypes.string,
};

LandingMediaKitsIndex.defaultProps = {
  themeMode: 'light',
};

export default LandingMediaKitsIndex;
